import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CollectionPage from '~/components/module/setup/CollectionPage';
import { RepeatLabel, YesNo, str } from '~/components/module/setup/cells';
import { ACTIVITY_REPEAT_TYPES, createActivityAdapter } from '~/apis/activity';
import { getTimezoneAbbr } from '~/apis/geofence';
import { useSeniorTimezone } from '~/utils/hooks/useSeniorTimezone';
import { FormValues, IFormField } from '~/utils/interface/setup';

const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const Recurrence = ({ form }: { form: FormValues }) => {
  const { t } = useTranslation();
  const days =
    form.repeatType === 'WEEKLY'
      ? str(form.repeatWeekOn)
          .split(',')
          .filter(Boolean)
          .map((day) => t(`pages.setup.weekdays.${WEEKDAY_KEYS[Number(day)]}`))
          .join(', ')
      : '';

  return (
    <>
      <RepeatLabel value={form.repeatType} />
      {days && <span className='text-muted-foreground'> ({days})</span>}
    </>
  );
};

const ActivitiesTable = ({ timezone }: { timezone: string }) => {
  const { t } = useTranslation();
  const adapter = useMemo(() => createActivityAdapter(timezone), [timezone]);

  const fields = useMemo<IFormField[]>(
    () => [
      { name: 'descriptions', labelKey: 'pages.setup.activities.description', type: 'text', required: true },
      { name: 'date', labelKey: 'pages.setup.activities.date', type: 'date', required: true },
      {
        name: 'time',
        labelKey: 'pages.setup.activities.time',
        // the senior's zone, shown when it differs from this computer's (as the app does)
        label:
          timezone === Intl.DateTimeFormat().resolvedOptions().timeZone
            ? t('pages.setup.activities.time')
            : `${t('pages.setup.activities.time')} (${getTimezoneAbbr(timezone)})`,
        type: 'time',
        required: true,
      },
      {
        name: 'repeatType',
        labelKey: 'pages.setup.activities.recurrence',
        type: 'select',
        required: true,
        options: ACTIVITY_REPEAT_TYPES.map((value) => ({ value, labelKey: `pages.setup.repeat.${value}` })),
      },
      {
        // the app sends this list with Sunday = 0
        name: 'repeatWeekOn',
        labelKey: 'pages.setup.activities.repeatOn',
        type: 'weekdays',
        weekStart: 0,
        required: true,
        showWhen: (values) => values.repeatType === 'WEEKLY',
      },
      { name: 'isNotificationSenior', labelKey: 'pages.setup.activities.noticeSenior', type: 'checkbox' },
      { name: 'isNotificationAdmin', labelKey: 'pages.setup.activities.noticeAdmin', type: 'checkbox' },
    ],
    [timezone, t]
  );

  return (
    <CollectionPage
      i18nPrefix='pages.setup.activities'
      module='event'
      adapter={adapter}
      fields={fields}
      defaultForm={{ repeatType: 'ONCE', repeatWeekOn: '', isNotificationSenior: false, isNotificationAdmin: false }}
      getTitle={(form) => str(form.descriptions)}
      columns={[
        { key: 'descriptions', labelKey: 'pages.setup.activities.description', render: (form) => <span className='font-medium text-foreground'>{str(form.descriptions)}</span> },
        { key: 'date', labelKey: 'pages.setup.activities.date', render: (form) => str(form.date) },
        { key: 'time', labelKey: 'pages.setup.activities.time', render: (form) => str(form.time) },
        { key: 'recurrence', labelKey: 'pages.setup.activities.recurrence', render: (form) => <Recurrence form={form} /> },
        { key: 'senior', labelKey: 'pages.setup.activities.noticeSenior', render: (form) => <YesNo value={form.isNotificationSenior} /> },
        { key: 'admin', labelKey: 'pages.setup.activities.noticeAdmin', render: (form) => <YesNo value={form.isNotificationAdmin} /> },
      ]}
    />
  );
};

// Mounts once the senior's time zone is known, so the adapter is built a single time.
const Activities = () => {
  const { t } = useTranslation();
  const timezone = useSeniorTimezone();

  if (!timezone) return <p className='text-sm text-muted-foreground'>{t('common.loading')}</p>;

  return <ActivitiesTable timezone={timezone} />;
};

export default Activities;
